/**
 * Settings Context for PetMas
 *
 * Provides site settings (analytics, SEO, features, etc.) to all components.
 * Settings are fetched once on app load and cached.
 */

import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { useSettings } from '../api';
import type { PublicSettings } from '@petmas/shared';
import { CHECKOUT_CONSTANTS, UI_CONSTANTS } from '@petmas/shared';

// Default settings used while loading or on error
const defaultSettings: PublicSettings = {
  analytics: {
    ga4Enabled: false,
    ga4MeasurementId: null,
    gtmEnabled: false,
    gtmContainerId: null,
    fbPixelEnabled: false,
    fbPixelId: null,
  },
  seo: {
    siteName: 'PetMas',
    siteDescription: 'Tienda de mascotas en Peru - Alimentos, accesorios y mas para perros y gatos',
    defaultOgImage: '/og-image.jpg',
    organization: {
      name: 'PetMas',
      address: {
        street: '',
        city: '',
        region: '',
        postalCode: '',
        country: 'PE',
      },
      phone: [],
      email: '',
      hours: '',
      social: {},
    },
    schemaProductEnabled: true,
    schemaBreadcrumbEnabled: true,
    schemaOrganizationEnabled: true,
  },
  checkout: {
    bsaleStoreDomain: 'www.petmas.pe',
    checkoutReturnUrl: 'https://petmas.pe/pedidos/confirmacion',
    freeShippingThreshold: CHECKOUT_CONSTANTS.DEFAULT_FREE_SHIPPING_THRESHOLD,
    maxCartQuantity: CHECKOUT_CONSTANTS.DEFAULT_MAX_CART_QUANTITY,
  },
  features: {
    aiChatbotEnabled: true,
    stockByLocationEnabled: false,
    notifyMeEnabled: false,
    blogEnabled: false,
    googleSigninEnabled: false,
    googleClientId: null,
    newsletterEnabled: true,
  },
  hero: {
    autoplayEnabled: true,
    autoplayInterval: UI_CONSTANTS.HERO_AUTOPLAY_INTERVAL_MS,
    showIndicators: true,
    showArrows: true,
  },
  heroSlides: [],
};

interface SettingsContextType {
  settings: PublicSettings;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, refetch } = useSettings();

  const settings = data ?? defaultSettings;

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isLoading,
        isError,
        refetch,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useAppSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useAppSettings must be used within a SettingsProvider');
  }
  return context;
}

// Convenience hooks for specific settings categories
export function useAnalyticsSettings() {
  const { settings } = useAppSettings();
  return settings.analytics;
}

export function useSEOSettings() {
  const { settings } = useAppSettings();
  return settings.seo;
}

export function useCheckoutSettings() {
  const { settings } = useAppSettings();
  return settings.checkout;
}

export function useFeatureFlags() {
  const { settings } = useAppSettings();
  return settings.features;
}

export function useHeroSettings() {
  const { settings } = useAppSettings();
  return { settings: settings.hero, slides: settings.heroSlides };
}

// Feature flag convenience hooks - only for boolean flags
type BooleanFeatureFlags = {
  [K in keyof PublicSettings['features']]: PublicSettings['features'][K] extends boolean ? K : never;
}[keyof PublicSettings['features']];

export function useFeatureEnabled(feature: BooleanFeatureFlags): boolean {
  const { settings } = useAppSettings();
  return settings.features[feature] as boolean;
}
